import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { API_URL } from '../config';
import { toApiError } from '../lib/api-error';
import { useAuth } from '../contexts/AuthContext';

const PROPERTY_STORAGE_KEY = 'flowDesigner:selectedPropertyIds';
const LEGACY_PROPERTY_STORAGE_KEY = 'flowDesigner:selectedPropertyId';

type StoredSelections = Record<string, string>;

const readStoredSelections = (): StoredSelections => {
  if (typeof window === 'undefined') {
    return {};
  }
  if (window.localStorage.getItem(LEGACY_PROPERTY_STORAGE_KEY)) {
    window.localStorage.removeItem(LEGACY_PROPERTY_STORAGE_KEY);
  }
  const raw = window.localStorage.getItem(PROPERTY_STORAGE_KEY);
  if (!raw) {
    return {};
  }
  try {
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
};

const persistSelections = (selections: StoredSelections) => {
  if (typeof window === 'undefined') {
    return;
  }
  window.localStorage.setItem(PROPERTY_STORAGE_KEY, JSON.stringify(selections));
};

export type UseOrgPropertySelectionOptions = {
  onPropertyChange?: (propertyId: string | null, meta: { previousPropertyId: string | null }) => void;
};

export const useOrgPropertySelection = (
  options: UseOrgPropertySelectionOptions = {}
) => {
  const { onPropertyChange } = options;
  const { user } = useAuth();
  const currentOrgId = user?.orgId ?? null;

  const initialSelections = React.useMemo(() => readStoredSelections(), []);
  const selectionsRef = React.useRef<StoredSelections>(initialSelections);

  const [propertyId, setPropertyId] = React.useState<string | null>(null);

  const persistSelection = React.useCallback((orgId: string, nextPropertyId: string | null) => {
    if (!orgId) {
      return;
    }
    const nextSelections = { ...selectionsRef.current };
    if (nextPropertyId) {
      nextSelections[orgId] = nextPropertyId;
    } else {
      delete nextSelections[orgId];
    }
    selectionsRef.current = nextSelections;
    persistSelections(nextSelections);
  }, []);

  type PropertyListItem = {
    id: string;
    name: string;
    baseUrl?: string | null;
    description?: string | null;
  };

  const propertiesQuery = useQuery({
    queryKey: ['properties', currentOrgId],
    enabled: !!currentOrgId,
    queryFn: async (): Promise<{ items: PropertyListItem[] }> => {
      const response = await fetch(`${API_URL}/org/properties`, { credentials: 'include' });
      if (!response.ok) {
        throw await toApiError(response, 'Failed to fetch applications');
      }
      return response.json();
    },
  });

  // Reset property selection whenever org changes
  React.useEffect(() => {
    if (!currentOrgId) {
      if (propertyId !== null) {
        setPropertyId(null);
      }
      return;
    }
    const storedForOrg = selectionsRef.current[currentOrgId] ?? null;
    if (propertyId !== storedForOrg) {
      setPropertyId(storedForOrg ?? null);
    }
  }, [currentOrgId, propertyId]);

  // Ensure selected property stays in sync with available properties
  // Use a ref to track if we've already initialized to prevent infinite loops
  const hasSyncedPropertiesRef = React.useRef(false);
  const previousDataRef = React.useRef(propertiesQuery.data);

  React.useEffect(() => {
    if (!currentOrgId) {
      hasSyncedPropertiesRef.current = false;
      return;
    }
    if (!propertiesQuery.isSuccess) {
      return;
    }

    // Reset sync flag if properties data actually changed
    if (previousDataRef.current !== propertiesQuery.data) {
      hasSyncedPropertiesRef.current = false;
      previousDataRef.current = propertiesQuery.data;
    }

    // Skip if we've already synced for this data
    if (hasSyncedPropertiesRef.current) {
      return;
    }

    const items = propertiesQuery.data?.items ?? [];
    if (!Array.isArray(items) || items.length === 0) {
      if (propertyId !== null) {
        setPropertyId(null);
      }
      if (selectionsRef.current[currentOrgId]) {
        persistSelection(currentOrgId, null);
      }
      hasSyncedPropertiesRef.current = true;
      return;
    }

    const validIds = new Set(items.map((item) => item.id));

    if (propertyId && validIds.has(propertyId)) {
      if (selectionsRef.current[currentOrgId] !== propertyId) {
        persistSelection(currentOrgId, propertyId);
      }
      hasSyncedPropertiesRef.current = true;
      return;
    }

    const storedSelection = selectionsRef.current[currentOrgId];
    if (storedSelection && validIds.has(storedSelection)) {
      if (propertyId !== storedSelection) {
        setPropertyId(storedSelection);
      }
      hasSyncedPropertiesRef.current = true;
      return;
    }

    const fallbackId = items[0]!.id;
    if (propertyId !== fallbackId) {
      setPropertyId(fallbackId);
    }
    persistSelection(currentOrgId, fallbackId);
    hasSyncedPropertiesRef.current = true;
  }, [currentOrgId, propertiesQuery.isSuccess, propertiesQuery.data, persistSelection, propertyId]);

  const selectProperty = React.useCallback(
    (nextPropertyId: string | null) => {
      if (propertyId === nextPropertyId) {
        return;
      }
      setPropertyId(nextPropertyId);
      if (currentOrgId) {
        persistSelection(currentOrgId, nextPropertyId);
      }
      // Reset sync flag to allow the effect to run again if needed
      hasSyncedPropertiesRef.current = false;
    },
    [propertyId, currentOrgId, persistSelection]
  );

  const previousPropertyIdRef = React.useRef<string | null>(null);
  React.useEffect(() => {
    if (previousPropertyIdRef.current === propertyId) {
      return;
    }
    const previousPropertyId = previousPropertyIdRef.current;
    previousPropertyIdRef.current = propertyId;
    onPropertyChange?.(propertyId, { previousPropertyId });
  }, [propertyId, onPropertyChange]);

  const propertyOptions = React.useMemo(
    () =>
      (propertiesQuery.data?.items ?? []).map((prop) => ({
        id: prop.id,
        label: prop.name,
      })),
    [propertiesQuery.data?.items]
  );

  return {
    currentOrgId,
    propertyId,
    selectProperty,
    propertyOptions,
    propertiesQuery,
  };
};
